<template>
  <div>
    <b-card
      v-if="isActivated"
    >
      <h3 class="h4">
        <i class="fa fa-fw fa-lock mr-1"></i>
        You are using two-factor authentication
        <hr>
      </h3>
      <p>
        Two-factor authentication is adding an extra layer of security to your account.
        It helps us know it's actually you.
      </p>
      <b-button
        class="text-danger px-0"
        variant="link"
        @click="startRemoveTwoFactorAuth"
      >
        Remove Two-Factor Authentication
      </b-button>
    </b-card>

    <b-card
      v-if="!isActivated"
    >
      <h2 class="h4">
        <i class="fa fa-fw fa-unlock mr-1"></i>
        You are not using two-factor authentication
        <hr>
      </h2>
      <p>
        Two-factor authentication will add an extra layer of security to your account.
        It helps us know it's actually you.
      </p>
      <b-button
        class=""
        variant="primary"
        @click.prevent="startTwoFactorAuthSetup"
      >
        Enable Two-Factor Authentication
      </b-button>
    </b-card>

    <b-modal
      title="Enable Two-Factor Authentication"
      hide-footer
      :visible="step === 'about'"
      no-close-on-backdrop
      no-close-on-esc
      @close="step = null"
    >
      <p class="mb-4">
        To add two-factor authentication to your account, you first
        need to add Google Authenticator to your mobile device.
        Go ahead and do that now, we'll wait.
      </p>

      <b-button
        block
        variant="white"
        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
      >
        Download for iOS
      </b-button>
      <b-button
        block
        variant="white"
        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US"
      >
        Download for Android
      </b-button>
      <hr>
      <b-button
        block
        variant="primary"
        @click="step = 'verify'"
      >
        Continue
      </b-button>
    </b-modal>

    <b-modal
      title="Enable Two-Factor Authentication"
      hide-footer
      :visible="step === 'verify'"
      no-close-on-backdrop
      no-close-on-esc
      @close="step = null"
    >
      <div
        v-if="verified === false"
        class="alert alert-danger text-center"
      >
        Your password is incorrect.
      </div>

      <p class="mb-4 text-center">
        To continue, please verify it's you by entering your <b>accessRedwood</b> password below:
      </p>

      <div class="form-group">
        <label for="password" class="font-weight-bold">Password</label>
        <input
          id="password"
          v-model="password"
          type="password"
          class="form-control"
          required
        >
      </div>
      <hr>
      <div class="d-flex justify-content-between">
        <a
          :href="urls.users.showForgotPassword()"
          class="btn btn-link px-0"
        >
          Forgot Your Password?
        </a>
        <b-button
          variant="primary"
          :disabled="verifyingPassword"
          @click.prevent="verifyPassword()"
        >
          {{ verifyingPassword ? 'Verifying...' : 'Continue' }}
        </b-button>
      </div>
    </b-modal>

    <b-modal
      title="Enable Two-Factor Authentication"
      hide-footer
      :visible="step === 'code'"
      no-close-on-backdrop
      no-close-on-esc
      @close="step = null"
    >
      <p>
        Now, use Google Authenticator to scan the QR-code below, then
        enter the 6-digit code given to you by the app.
      </p>
      <div class="text-center">
        <slot name="qr-code"></slot>
      </div>
      <div class="form-group">
        <label for="code" class="font-weight-bold">One-Time Code</label>
        <input
          id="code"
          v-model="code"
          type="text"
          class="form-control"
          name="code"
          required
        >
      </div>
      <hr>
      <b-button
        block
        variant="primary"
        :disabled="savingSecret"
        @click.prevent="saveTwoFactorAuthSecret()"
      >
        {{ savingSecret ? 'Saving...' : 'Continue' }}
      </b-button>
    </b-modal>

    <b-modal
      title="Disable Two-Factor Authentication"
      hide-footer
      :visible="step === 'disable'"
      no-close-on-backdrop
      no-close-on-esc
      @close="step = null"
    >
      <p>
        To remove two-factor authentication, enter your current
        6-digit one-time code below:
      </p>
      <div class="form-group">
        <label for="code" class="font-weight-bold">One-Time Code</label>
        <input
          id="code"
          v-model="code"
          type="text"
          class="form-control"
          name="code"
          required
        >
      </div>
      <hr>
      <b-button
        block
        variant="primary"
        :disabled="removing"
        @click.prevent="removeTwoFactorAuth()"
      >
        {{ removing ? 'Removing...' : 'Continue' }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash'
import { verifyPassword, saveTwoFactorAuthSecret, removeTwoFactorAuth } from '@/api'
import urls from '@/api/urls'

export default {
  props: {
    userId: {
      type: [String, Number],
      default: null
    },
    state: {
      type: [Object, Array],
      default () {
        return {
          activated: null,
          valid: null,
          secret: null
        }
      }
    }
  },
  data () {
    return {
      step: null,
      password: null,
      urls,
      verifyingPassword: false,
      code: null,
      verified: null,
      savingSecret: null,
      removing: null
    }
  },
  computed: {
    isActivated () {
      return !!get(this.state, 'activated')
    }
  },
  watch: {
    step () {
      if (this.step === null) {
        this.password = null
        this.code = null
        this.verified = null
        this.verifyingPassword = null
        this.savingSecret = null
        this.removing = null
      }
    }
  },
  mounted () {

  },
  methods: {
    startTwoFactorAuthSetup () {
      this.step = 'about'
    },
    startRemoveTwoFactorAuth () {
      this.step = 'disable';
    },
    async verifyPassword () {
      this.verifyingPassword = true
      let response = await verifyPassword(this.password, this.userId)
      this.verifyingPassword = false
      this.verified = response.data.verified
      if (this.verified) {
        this.step = 'code'
      }
    },
    async saveTwoFactorAuthSecret () {
      this.savingSecret = true
      try {
        let response = await saveTwoFactorAuthSecret(this.password, this.code, this.userId)
        document.location.reload()
      } catch (e) {
        // TODO: try to display the error
        this.savingSecret = false
      } finally {
        //
      }
    },
    async removeTwoFactorAuth () {
      this.removing = true
      try {
        let response = await removeTwoFactorAuth(this.code, this.userId)
        document.location.reload()
      } catch (e) {
        // TODO: try to display the error
        this.removing = false
      } finally {
        //
      }
    }
  }
}
</script>